<template>
  <div class="container">
    <a-card title="全院项目回款统计">
      <!-- 年度选择 -->
      <div class="header">
        <a-select
          v-model="selectedYear"
          style="width: 120px"
          @change="onYearChange"
        >
          <a-select-option v-for="year in yearOptions" :key="year" :value="year">
            {{ year }}年
          </a-select-option>
        </a-select>
        <a-button type="link" @click="goBack">返回</a-button>
      </div>

      <Padding />

      <!-- 设计项目回款汇总 -->
      <div>
        <h3>总院设计项目回款汇总</h3>
        <a-table
          bordered
          :data-source="designList"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
          :row-class-name="rowClassName"
        >
          <a-table-column title="部门" data-index="deptName" width="150px" />
          <a-table-column-group>
            <template #title>
              <span>{{ designMonthTitle }}</span>
            </template>
            <a-table-column
              v-for="week in weekColumns"
              :key="'design-plan-' + week.weekNumber"
              :title="`第${week.weekNumber}周计划`"
              width="100px"
              align="right"
            >
              <template #default="text, record">
                <span>{{ getPlanAmount(record, week.weekNumber) }}</span>
              </template>
            </a-table-column>
            <a-table-column width="100px" align="right">
              <template #title>
                <span>{{ designMonth }}月实际</span>
              </template>
              <template #default="text, record">
                <span>{{ record.actualAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column width="100px" align="right">
              <template #title>
                <span>1-{{ designMonth }}月缺口</span>
              </template>
              <template #default="text, record">
                <span :style="{ color: record.gapAmount < 0 ? 'red' : 'green' }">
                  {{ record.gapAmount || 0 }}
                </span>
              </template>
            </a-table-column>
          </a-table-column-group>
          <a-table-column-group title="历史累计回款">
            <a-table-column v-if="designMonth > 1" width="120px" align="right">
              <template #title>
                <span>1-{{ designMonth - 1 }}月累计</span>
              </template>
              <template #default="text, record">
                <span>{{ record.yearAccumulatedBeforeCurrentMonth || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column width="120px" align="right">
              <template #title>
                <span>本年累计(1-{{ designMonth }}月)</span>
              </template>
              <template #default="text, record">
                <span>{{ record.yearAccumulatedActual || 0 }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
        </a-table>

        <Padding />
      </div>

      <!-- EPC项目回款汇总 -->
      <div>
        <h3>EPC项目回款汇总</h3>
        <a-table
          bordered
          :data-source="epcList"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
          :row-class-name="rowClassName"
        >
          <a-table-column title="部门" data-index="deptName" width="150px" />
          <a-table-column-group>
            <template #title>
              <span>{{ epcMonthTitle }}</span>
            </template>
            <a-table-column
              v-for="week in weekColumns"
              :key="'epc-plan-' + week.weekNumber"
              :title="`第${week.weekNumber}周计划`"
              width="100px"
              align="right"
            >
              <template #default="text, record">
                <span>{{ getPlanAmount(record, week.weekNumber) }}</span>
              </template>
            </a-table-column>
            <a-table-column width="100px" align="right">
              <template #title>
                <span>{{ epcMonth }}月实际</span>
              </template>
              <template #default="text, record">
                <span>{{ record.actualAmount || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column width="100px" align="right">
              <template #title>
                <span>1-{{ epcMonth }}月缺口</span>
              </template>
              <template #default="text, record">
                <span :style="{ color: record.gapAmount < 0 ? 'red' : 'green' }">
                  {{ record.gapAmount || 0 }}
                </span>
              </template>
            </a-table-column>
          </a-table-column-group>
          <a-table-column-group title="历史累计回款">
            <a-table-column v-if="epcMonth > 1" width="120px" align="right">
              <template #title>
                <span>1-{{ epcMonth - 1 }}月累计</span>
              </template>
              <template #default="text, record">
                <span>{{ record.yearAccumulatedBeforeCurrentMonth || 0 }}</span>
              </template>
            </a-table-column>
            <a-table-column width="120px" align="right">
              <template #title>
                <span>本年累计(1-{{ epcMonth }}月)</span>
              </template>
              <template #default="text, record">
                <span>{{ record.yearAccumulatedActual || 0 }}</span>
              </template>
            </a-table-column>
          </a-table-column-group>
        </a-table>
      </div>

      <!-- 空状态 -->
      <a-empty v-if="!loading && designList.length === 0 && epcList.length === 0" description="暂无数据" />
    </a-card>
  </div>
</template>

<script>
import { yearlyStatistics, getWeeks } from "@/api/finance/project-payment-plan";
import moment from "moment";

export default {
  data() {
    return {
      selectedYear: null,
      statistics: null,
      loading: false,
      weekColumns: [],
      yearOptions: [],
    };
  },

  computed: {
    // 设计类部门列表
    designList() {
      if (!this.statistics || !this.statistics.designList) {
        return [];
      }
      return this.statistics.designList;
    },

    // EPC类部门列表
    epcList() {
      if (!this.statistics || !this.statistics.epcList) {
        return [];
      }
      return this.statistics.epcList;
    },

    // 设计类月份（取第一个部门的月份）
    designMonth() {
      if (this.designList.length > 0 && this.designList[0].month) {
        return this.designList[0].month;
      }
      return moment().month() + 1;
    },

    // EPC类月份（取第一个部门的月份）
    epcMonth() {
      if (this.epcList.length > 0 && this.epcList[0].month) {
        return this.epcList[0].month;
      }
      return moment().month() + 1;
    },

    // 设计类月份标题
    designMonthTitle() {
      return `${this.designMonth}月汇总`;
    },

    // EPC类月份标题
    epcMonthTitle() {
      return `${this.epcMonth}月汇总`;
    },
  },

  created() {
    this.initYearOptions();
    this.initFromQuery();
    this.getStatistics();
  },

  methods: {
    // 初始化年度选项
    initYearOptions() {
      const currentYear = moment().year();
      this.yearOptions = [];
      for (let i = currentYear; i >= currentYear - 5; i--) {
        this.yearOptions.push(i.toString());
      }
    },

    // 从query参数初始化
    initFromQuery() {
      const query = this.$route.query;
      if (query.year) {
        this.selectedYear = query.year;
      } else {
        this.selectedYear = moment().year().toString();
      }
    },

    // 获取统计数据
    async getStatistics() {
      this.loading = true;
      try {
        // 获取统计数据
        const statsRes = await yearlyStatistics(this.selectedYear);
        if (statsRes) {
          this.statistics = statsRes;
          console.log('获取统计数据成功', this.statistics);
          
          // 从第一个部门的数据中获取周数
          const firstDept = this.designList.length > 0 ? this.designList[0] : 
                           (this.epcList.length > 0 ? this.epcList[0] : null);
          if (firstDept && firstDept.paymentPlanList) {
            this.weekColumns = firstDept.paymentPlanList.map((week) => ({
              weekNumber: week.weekNumber,
            }));
            console.log('从第一个部门获取周信息', this.weekColumns);
          } else {
            // 如果没有周数据，尝试从API获取
            const month = firstDept && firstDept.month ? firstDept.month : moment().month() + 1;
            const yearMonth = `${this.selectedYear}-${String(month).padStart(2, '0')}`;
            const weeksRes = await getWeeks(yearMonth);
            if (weeksRes && Array.isArray(weeksRes)) {
              this.weekColumns = weeksRes.map((week) => ({
                weekNumber: week.weekNumber,
              }));
              console.log('获取周信息成功', this.weekColumns);
            }
          }
        }
      } catch (err) {
        console.error("获取数据失败", err);
      } finally {
        this.loading = false;
      }
    },

    // 年度选择变化
    onYearChange(year) {
      this.selectedYear = year;
      this.getStatistics();
    },

    // 获取计划金额
    getPlanAmount(record, weekNumber) {
      if (record.paymentPlanList) {
        const week = record.paymentPlanList.find(w => w.weekNumber === weekNumber);
        return week ? (week.planAmount || 0) : 0;
      }
      return 0;
    },

    // 行样式
    rowClassName(record) {
      return record.isSummary ? 'summary-row' : '';
    },

    // 返回上一页
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.container {
  padding: 16px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/deep/ .summary-row {
  background-color: #e8e8e8 !important;
}

/deep/ .summary-row td {
  background-color: #e8e8e8 !important;
  font-weight: bold;
}
</style>